import axios, { type AxiosError } from "axios";
import { getCookie } from "cookies-next/client";

interface ApiErrorPayload {
  errors?: string[];
  error?: string;
  message?: string;
  code?: string;
}

/** Disparado quando a API informa que a sessão do usuário não existe mais. */
export const SESSION_ENDED_EVENT = "simtro:session-ended";

export interface CustomError extends Error {
  status?: number;
  data?: unknown;
}

function readAccessToken() {
  if (typeof window === "undefined") return undefined;

  return (
    window.localStorage.getItem("access_token") ??
    getCookie("access_token")?.toString() ??
    undefined
  );
}

export const api = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_BASE_URL,
  headers: { "Content-Type": "application/json" },
  timeoutErrorMessage: "A requisicao demorou muito tempo para ser concluida.",
  timeout: 30000,
});

api.interceptors.request.use((config) => {
  if (config.data instanceof FormData) {
    delete config.headers["Content-Type"];
  }

  const accessToken = readAccessToken();

  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }

  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiErrorPayload>) => {
    const data = error.response?.data;
    const message =
      (Array.isArray(data?.errors) && data.errors[0]) ||
      data?.error ||
      data?.message ||
      error.response?.statusText ||
      error.message;

    // Sessão encerrada (expirou, novo login em outro dispositivo, troca de senha).
    // Só vale para requisições autenticadas; "permissão negada" também é 401, sem esse código.
    if (
      error.response?.status === 401 &&
      data?.code === "UNAUTHENTICATED" &&
      error.config?.headers?.Authorization &&
      typeof window !== "undefined"
    ) {
      window.dispatchEvent(
        new CustomEvent(SESSION_ENDED_EVENT, { detail: data.message }),
      );
    }

    const formattedError = new Error(message) as CustomError;

    formattedError.status = error.response?.status;
    formattedError.data = error.response?.data;

    return Promise.reject(formattedError);
  },
);
